/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

import { PipeTransform } from '@angular/core';

export enum DiffTypeItem {
    NEW = 'new',
    EDIT = 'edit',
    DELETED = 'deleted',
    NO_DIFF = 'no-diff',
}

/**
 * Types that map to types defined by deep-diff: 'N': new, 'E': edit, 'D': deleted.
 * Cutstom type: 'no-diff' if lhs and rhs are exactly the same or showing diff is not required.
 * Type 'A': type of "changes within array" from deep-diff, is not handled since avi-diff-grid is
 * dealing with plain texts only.
 */
export type TDiffType = DiffTypeItem.NEW | DiffTypeItem.EDIT | DiffTypeItem.DELETED | DiffTypeItem.NO_DIFF;

export type TAviDiffGridSourceObject = Record<string, string>;

/**
 * Interface for pipes to transform raw data to a diff grid source pair.
 * All pipes that are used to transform data to AviDiffGridRow[] must implement with this interface.
 * Since this interface extends PipeTransform, there no need to extend PipeTransform for custom
 * pipes.
 */
export interface IAviDiffGridSourcePairTransform extends PipeTransform {
    transform(value: any, ...args: any[]): IAviDiffGridSourcePair;
}

// diff source pair with a left object source and a right obejct source
export interface IAviDiffGridSourcePair {
    // a source object can be undefined if no data has been transformed
    leftSource?: TAviDiffGridSourceObject,
    rightSource?: TAviDiffGridSourceObject,
    // needed only when repeated keys are presented in the original data (ususally unprocessed
    // strings) and source objects are built with a diffPostfix to distinguish between them
    diffPostfix?: string,
    performNoDiff?: boolean,
}

export interface IAviDiffGridRow {
    left?: string,
    right?: string,
    type?: TDiffType,
    depth?: number,
}

export interface IAviDiffGridLayout {
    placeholderMessage?: string;
}

export interface IAviDiffGridConfigField {
    label: string;
}

export interface IAviDiffGridConfig {
    leftField: IAviDiffGridConfigField;
    rightField: IAviDiffGridConfigField;
    noDiff?: boolean; // true when diff is not required, show texts without background highlight
    layout?: IAviDiffGridLayout;
    isGridExpandable?: boolean;
}

export interface IAviDiffGridItem {
    kind: 'N' | 'D' | 'E' | 'A';
    lhs?: unknown;
    rhs?: unknown;
    path?: Array<string | number>;
    index?: number;
    item?: IAviDiffGridItem;
}
